/**
 * Tiny event bus so any page can pop the floating WhatsApp widget open on a
 * specific lead's chat (e.g. the WhatsApp button in the lead-detail header).
 * Same pattern as `app/sidebar-collapse.ts`.
 */

export interface OpenWhatsAppChatRequest {
  leadId: string;
  /** Display name for the chat header before the thread loads. */
  name?: string;
  /** Destination label - prefills template params. */
  destination?: string;
}

type Listener = (req: OpenWhatsAppChatRequest) => void;

const listeners = new Set<Listener>();

export const openWhatsAppForLead = (req: OpenWhatsAppChatRequest): void => {
  for (const l of listeners) l(req);
};

export const onOpenWhatsAppForLead = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Requests that can arrive BEFORE the widget is listening - a tap on a phone
 * notification cold-starts the app, and the launch handler fires while the
 * widget is still waiting on permissions. The latest one is parked and handed
 * to the first listener that subscribes, unless it has gone stale.
 */
const PENDING_TTL_MS = 2 * 60 * 1000;

const parkedChannel = <T>() => {
  const subscribers = new Set<(value: T) => void>();
  let parked: { value: T; at: number } | null = null;
  return {
    emit(value: T): void {
      if (subscribers.size === 0) {
        parked = { value, at: Date.now() };
        return;
      }
      for (const s of subscribers) s(value);
    },
    subscribe(listener: (value: T) => void): () => void {
      subscribers.add(listener);
      if (parked) {
        const { value, at } = parked;
        parked = null;
        if (Date.now() - at < PENDING_TTL_MS) listener(value);
      }
      return () => {
        subscribers.delete(listener);
      };
    },
  };
};

export interface PickUpOptions {
  /** The alert's "Answer" button: answer at once, don't just ring. */
  answer: boolean;
}

const conversationChannel = parkedChannel<string>();
const callChannel = parkedChannel<{ callId: string } & PickUpOptions>();

/** Pop the widget open on one conversation (phone notification tap). */
export const openWhatsAppConversation = (conversationId: string): void =>
  conversationChannel.emit(conversationId);

export const onOpenWhatsAppConversation = (listener: (conversationId: string) => void) =>
  conversationChannel.subscribe(listener);

/**
 * Bring up the answer screen for a ringing call (phone notification tap), or
 * answer it outright (the notification's "Answer" button).
 */
export const pickUpWhatsAppCall = (callId: string, opts: PickUpOptions = { answer: false }): void =>
  callChannel.emit({ callId, answer: opts.answer });

export const onPickUpWhatsAppCall = (listener: (callId: string, opts: PickUpOptions) => void) =>
  callChannel.subscribe(({ callId, answer }) => listener(callId, { answer }));
